import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import * as XLSX from "xlsx";
import { useHistory } from "react-router-dom";
import {
  ArrowBack,
  ArrowForward,
  CancelOutlined,
  CheckCircleOutline,
  DescriptionOutlined,
  FileDownloadOutlined,
  FileUploadOutlined,
  Replay,
  Search,
  StorageOutlined,
} from "@mui/icons-material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { detailCardSx } from "components/DesignSystem/RequestDetailField";
import { globalTextFieldSx, sharedTableBodyCellSx } from "components/DesignSystem/sharedUiPatterns";
import { PanelActionFooter, PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import {
  StatCard,
  designColors,
  designRadii,
  designShell,
  designType,
  designWeights,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import { tooltipStyleProps } from "services/constants/Styles";
import { showToast } from "services/utils/Status";
import { WizardStepper } from "pages/PayRoll/PayInputs/Shared/WizardStepper";
import { SelectField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { UAN_IMPORT_FIELDS, autoMapUanColumns, mappedUanRows, mappingByHeader, missingUanFields, normalise } from "./pfMembersData";
import { commitUanImport, downloadUanTemplate, fetchUanMapping, newRequestKey, previewUanImport } from "./pfMembersApi";

// Payroll > Statutory > PF > Import UAN: Upload File -> Map Columns ->
// Preview & Import, same layout as the ESI IP import and the Pay Inputs
// imports. The file is read and mapped in the browser; Map Columns > Next
// sends the mapped rows to POST uan-import/preview/ (nothing saved, the
// server returns a batch and a result per row). Error rows open an Edit
// panel; Re-Verify sends the corrected file as a new preview batch. Import
// commits the batch (POST uan-import/<batch>/commit/). The server saves the
// column mapping on preview for the next import.

const STEPS = ["Upload File", "Map Columns", "Preview & Import"];
const MAX_BYTES = 5 * 1024 * 1024;
const MAX_ROWS = 5000;
const TEMPLATE_SAMPLE_ROWS = 3;
const YES_NO_FIELDS = new Set(["previous_pf_member", "eps_member", "full_salary"]);
const YES_NO_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

const GUIDELINES = [
  "Use the provided template. Columns can be in any order and renamed — check the matches in the Map Columns step.",
  "Employee ID is the employee code (e.g. VST1005). Employee Name is only for checking and is not saved.",
  "UAN is the 12-digit number from the EPFO portal member list.",
  "Dates are DD/MM/YYYY. Months (Opt out from, Extra PF from month) are MM/YYYY; Opt out from also takes DOJ.",
  "Yes / No columns: Previous PF member, Pension (EPS) member, PF on full salary.",
  "Leave a cell blank to keep what is saved. Maximum 5,000 rows and 5 MB per file.",
];

const fieldSx = {
  ...globalTextFieldSx,
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: designColors.teal },
  "& .MuiInputBase-input": { fontSize: 14 },
};
const tableHeadSx = { ...designShell.tableHeadCellBase, py: 1, whiteSpace: "nowrap" };
const cellSx = { ...sharedTableBodyCellSx, py: 1 };
const errorText = "#DC2626";

const formatBytes = (bytes) => (bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`);
const textOf = (value) => (value === null || value === undefined ? "" : String(value).trim());
const shown = (value) => textOf(value) || "–";

// Every cell as the text the user sees, dates as DD/MM/YYYY. Also used by
// the ESI IP import and the PT slab upload.
export const readSheet = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the file."));
    reader.onload = () => {
      try {
        const book = XLSX.read(reader.result, { type: "array", cellNF: true, raw: true });
        const sheet = book.Sheets[book.SheetNames[0]];
        Object.keys(sheet).forEach((address) => {
          const cell = sheet[address];
          if (address[0] === "!" || cell.t !== "n") return;
          if (cell.z && XLSX.SSF.is_date(cell.z)) {
            const day = XLSX.SSF.parse_date_code(cell.v);
            if (!day) return;
            cell.t = "s";
            cell.v = `${String(day.d).padStart(2, "0")}/${String(day.m).padStart(2, "0")}/${day.y}`;
          } else {
            // UANs are 12-digit numbers; keep them whole, not 1.00123E+11.
            cell.t = "s";
            cell.v = String(Math.round(cell.v) === cell.v ? cell.v.toFixed(0) : cell.v);
          }
          delete cell.w;
        });
        resolve(XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: "" }));
      } catch (error) {
        reject(new Error("This file could not be read. Use the template."));
      }
    };
    reader.readAsArrayBuffer(file);
  });

// Server results (row = 1-based position in the rows sent) put back on the
// file rows. Valid / Change / No change rows pass; Error rows don't.
const withResults = (rows, results) => {
  const byRow = {};
  results.forEach((item, index) => {
    byRow[Number(item.row) || index + 1] = item;
  });
  return rows.map((row) => {
    const answer = byRow[row.rowNumber];
    return { ...row, result: answer?.result || "Valid", message: answer?.message || "" };
  });
};
const isError = (row) => row.result === "Error";
const willSave = (row) => row.result === "Valid" || row.result === "Change";

// ---- Shared bits ----

const Card = ({ title, helper, action, children, sx }) => (
  <Mui.Box sx={{ ...detailCardSx, ...sx }}>
    {title ? (
      <Mui.Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1} sx={{ mb: 1.5 }}>
        <Mui.Box>
          <Mui.Typography sx={designType.sectionHeading}>{title}</Mui.Typography>
          {helper ? <Mui.Typography sx={{ ...designType.helperText, mt: 0.25 }}>{helper}</Mui.Typography> : null}
        </Mui.Box>
        {action}
      </Mui.Stack>
    ) : null}
    {children}
  </Mui.Box>
);

const FileCard = ({ file, onChange }) => (
  <Mui.Stack direction="row" spacing={1.5} alignItems="center" sx={{ ...detailCardSx, backgroundColor: designColors.slateBg }}>
    <Mui.Box sx={{ width: 40, height: 40, borderRadius: designRadii.iconButton, backgroundColor: "#E7F8EF", color: "#12B76A", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <DescriptionOutlined />
    </Mui.Box>
    <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
      <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }} noWrap>{file.name}</Mui.Typography>
      <Mui.Typography sx={designType.captionText}>{file.rows.length.toLocaleString("en-IN")} rows · {formatBytes(file.size)}</Mui.Typography>
    </Mui.Box>
    {onChange ? <SecondaryButton onClick={onChange} sx={{ minHeight: 36, minWidth: 0 }}>Change File</SecondaryButton> : null}
  </Mui.Stack>
);

const TabButtons = ({ tabs, tab, counts, onChange }) => (
  <Mui.Stack direction="row" spacing={1} sx={{ overflowX: "auto" }} role="tablist">
    {tabs.map((item) => {
      const active = item.key === tab;
      return (
        <Mui.ButtonBase
          key={item.key}
          role="tab"
          aria-selected={active}
          onClick={() => onChange(item.key)}
          sx={{ px: 1.5, py: 0.75, flexShrink: 0, borderRadius: designRadii.control, border: `1px solid ${active ? designColors.tealDark : designColors.slate200}`, backgroundColor: active ? designColors.tealTint : designColors.white }}
        >
          <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: active ? designWeights.semibold : designWeights.medium, color: active ? designColors.tealDark : designColors.slate700, whiteSpace: "nowrap" }}>
            {item.label} ({counts[item.key].toLocaleString("en-IN")})
          </Mui.Typography>
        </Mui.ButtonBase>
      );
    })}
  </Mui.Stack>
);

const usePaged = (items, resetKey) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  useEffect(() => setPage(0), [resetKey]);
  return {
    pageItems: items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    footer: (
      <Mui.Box sx={{ px: 2, py: 1, borderTop: `1px solid ${designColors.slate200}` }}>
        {paginationFooter({
          page,
          rowsPerPage,
          total: items.length,
          itemLabel: "rows",
          onPageChange: setPage,
          onRowsPerPageChange: (event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          },
          rowsPerPageOptions,
        })}
      </Mui.Box>
    ),
  };
};

// ---- Step 1: Upload ----

const UploadStep = ({ file, error, onFile, onTemplate, templateBusy }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const pick = (files) => {
    if (files?.[0]) onFile(files[0]);
  };

  return (
    <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 3fr) minmax(0, 2fr)" }, gap: 1.5, alignItems: "stretch" }}>
      <Mui.Stack spacing={1.5} sx={{ minHeight: "100%" }}>
        <Mui.Box
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") inputRef.current?.click();
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            pick(event.dataTransfer.files);
          }}
          sx={{
            ...detailCardSx,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
            px: 3,
            textAlign: "center",
            cursor: "pointer",
            borderStyle: "dashed",
            borderWidth: 2,
            borderColor: dragging ? designColors.teal : designColors.slate300,
            backgroundColor: dragging ? designColors.tealTint : designColors.white,
            "&:hover": { borderColor: designColors.teal },
          }}
        >
          <input
            ref={inputRef}
            hidden
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(event) => {
              pick(event.target.files);
              event.target.value = "";
            }}
          />
          <FileUploadOutlined sx={{ fontSize: 40, color: designColors.teal }} />
          <Mui.Typography sx={{ ...designType.sectionHeading, fontSize: 16, mt: 1.5 }}>Drag and drop your file here</Mui.Typography>
          <Mui.Typography sx={{ ...designType.helperText, mt: 0.5 }}>
            or <Mui.Box component="span" sx={{ color: designColors.tealDark, fontWeight: designWeights.semibold }}>click to browse</Mui.Box>
          </Mui.Typography>
          <Mui.Typography sx={{ ...designType.captionText, mt: 2 }}>Supported formats: Excel (.xlsx, .xls), CSV (.csv) · Max 5 MB</Mui.Typography>
          <Mui.Button
            startIcon={templateBusy ? <Mui.CircularProgress size={16} /> : <FileDownloadOutlined />}
            disabled={templateBusy}
            onClick={(event) => {
              event.stopPropagation();
              onTemplate();
            }}
            sx={{ mt: 2, textTransform: "none", fontWeight: designWeights.semibold, color: designColors.tealDark }}
          >
            Download UAN Template
          </Mui.Button>
        </Mui.Box>
        {file ? <FileCard file={file} /> : null}
        {error ? <RequestInfoBanner tone="danger" title="Cannot use this file" body={error} /> : null}
      </Mui.Stack>

      <Mui.Stack spacing={1.5} sx={{ minHeight: "100%" }}>
        <RequestInfoBanner title="Template Information" body="Use the UAN template to add or update UAN, PF joining, EPS, date of birth, opt-out and Extra PF for PF members. The template includes the columns with 3 example rows." />
        <Card sx={{ flex: 1 }}>
          <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <DescriptionOutlined sx={{ fontSize: 20, color: designColors.tealDark }} />
            <Mui.Typography sx={designType.sectionHeading}>UAN Import Guidelines</Mui.Typography>
          </Mui.Stack>
          <Mui.Stack spacing={1}>
            {GUIDELINES.map((line, index) => (
              <Mui.Stack key={line} direction="row" spacing={1.25} alignItems="flex-start">
                <Mui.Box sx={{ ...designType.captionText, flexShrink: 0, width: 20, height: 20, borderRadius: "50%", backgroundColor: designColors.slate100, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {index + 1}
                </Mui.Box>
                <Mui.Typography sx={designType.helperText}>{line}</Mui.Typography>
              </Mui.Stack>
            ))}
          </Mui.Stack>
        </Card>
      </Mui.Stack>
    </Mui.Box>
  );
};

// ---- Step 2: Map columns ----

const MapStep = ({ file, mapping, onMappingChange, onReset, onChangeFile }) => {
  const missing = missingUanFields(mapping);
  const usedFields = new Set(Object.values(mapping));
  return (
    <Mui.Stack spacing={1.5}>
      <FileCard file={file} onChange={onChangeFile} />
      <Card
        title="Map File Columns"
        helper="Columns were matched automatically by header name — they can be in any order and renamed. Check each match and change it if needed."
        action={
          <Mui.Button startIcon={<Replay />} onClick={onReset} sx={{ textTransform: "none", fontWeight: designWeights.semibold, color: designColors.tealDark }}>
            Reset Mapping
          </Mui.Button>
        }
      >
        <Mui.TableContainer sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: designRadii.card }}>
          <Mui.Table size="small">
            <Mui.TableHead>
              <Mui.TableRow>
                <Mui.TableCell sx={tableHeadSx}>File Column (from Excel)</Mui.TableCell>
                <Mui.TableCell sx={tableHeadSx}>Map to System Field</Mui.TableCell>
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {file.headers.map((header, index) => {
                const fieldKey = mapping[index] ?? "";
                return (
                  <Mui.TableRow key={`${header}-${index}`}>
                    <Mui.TableCell sx={{ ...cellSx, width: "45%" }}>{header || `Column ${index + 1}`}</Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>
                      {/* A field already mapped to another column is left out. */}
                      <SelectField
                        value={fieldKey}
                        onChange={(value) => onMappingChange(index, value)}
                        options={[
                          { value: "", label: "Do not import" },
                          ...UAN_IMPORT_FIELDS.filter((option) => !usedFields.has(option.key) || option.key === fieldKey).map((option) => ({
                            value: option.key,
                            label: `${option.label}${option.required ? " *" : ""}`,
                          })),
                        ]}
                        placeholder="Do not import"
                      />
                    </Mui.TableCell>
                  </Mui.TableRow>
                );
              })}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.TableContainer>
        {missing.length ? (
          <Mui.Box sx={{ mt: 2 }}>
            <RequestInfoBanner tone="warning" title="Required fields not mapped" body={`Map a column to: ${missing.map((item) => item.label).join(", ")}.`} />
          </Mui.Box>
        ) : null}
      </Card>
    </Mui.Stack>
  );
};

// ---- Step 3: Preview & Import ----

const PREVIEW_TABS = [
  { key: "all", label: "All Rows" },
  { key: "valid", label: "Valid Rows" },
  { key: "error", label: "Error Rows" },
];

// Error rows are tinted red; clicking one opens the Edit panel.
const errorRowSx = {
  cursor: "pointer",
  backgroundColor: "#FEF2F2",
  "&:hover": { backgroundColor: "#FEE2E2" },
  "&:focus-visible": { outline: `2px solid ${errorText}`, outlineOffset: -2 },
};

const PreviewStep = ({ rows, fields, onFixRow }) => {
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const valid = rows.filter((row) => !isError(row));
  const invalid = rows.filter(isError);
  const counts = { all: rows.length, valid: valid.length, error: invalid.length };
  const changes = rows.filter((row) => row.result === "Change").length;
  const unchanged = rows.filter((row) => row.result === "No change").length;

  const list = useMemo(() => {
    const base = tab === "valid" ? valid : tab === "error" ? invalid : rows;
    const needle = query.trim().toLowerCase();
    return needle ? base.filter((row) => Object.values(row.raw).some((value) => textOf(value).toLowerCase().includes(needle))) : base;
  }, [tab, query, rows, valid, invalid]);
  const { pageItems, footer } = usePaged(list, `${tab}:${query}`);

  const notes = [
    changes ? `${changes.toLocaleString("en-IN")} row(s) change details already saved.` : "",
    unchanged ? `${unchanged.toLocaleString("en-IN")} row(s) match the saved details and will be skipped.` : "",
  ].filter(Boolean);

  return (
    <Mui.Stack spacing={1.5}>
      <Card title="Summary" helper={notes.join(" ") || "Valid rows add or update the PF member details."}>
        <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
          {/* Cards pick the rows shown below, same as the tabs. */}
          <StatCard icon={StorageOutlined} title="Total Rows" value={counts.all.toLocaleString("en-IN")} accent="#12B76A" tint="#E7F8EF" onClick={() => setTab("all")} isActive={tab === "all"} activeBorder />
          <StatCard icon={CheckCircleOutline} title="Valid Rows" value={counts.valid.toLocaleString("en-IN")} accent={designColors.tealDark} tint={designColors.tealTint} onClick={() => setTab("valid")} isActive={tab === "valid"} activeBorder />
          <StatCard icon={CancelOutlined} title="Error Rows" value={counts.error.toLocaleString("en-IN")} accent={errorText} tint="#FEF2F2" onClick={() => setTab("error")} isActive={tab === "error"} activeBorder />
        </Mui.Box>
        {invalid.length ? (
          <Mui.Box sx={{ mt: 1.5 }}>
            <RequestInfoBanner
              tone="warning"
              body={`${invalid.length.toLocaleString("en-IN")} row(s) have errors and will not be imported. Click a red row to correct it, or import only the valid rows.`}
            />
          </Mui.Box>
        ) : null}
      </Card>

      <Mui.Box sx={{ ...detailCardSx, p: 0, overflow: "hidden" }}>
        <Mui.Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} sx={{ p: 2 }}>
          <TabButtons tabs={PREVIEW_TABS} tab={tab} counts={counts} onChange={setTab} />
          <Mui.TextField
            size="small"
            placeholder="Search..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{ ...fieldSx, width: { xs: "100%", md: 260 } }}
            InputProps={{ startAdornment: <Mui.InputAdornment position="start"><Search sx={{ fontSize: 18, color: designColors.slate400 }} /></Mui.InputAdornment> }}
          />
        </Mui.Stack>
        <Mui.TableContainer sx={{ overflowX: "auto" }}>
          <Mui.Table size="small">
            <Mui.TableHead>
              <Mui.TableRow>
                <Mui.TableCell sx={tableHeadSx}>Row</Mui.TableCell>
                {fields.map((field) => <Mui.TableCell key={field.key} sx={tableHeadSx}>{field.label}</Mui.TableCell>)}
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {pageItems.length === 0 ? (
                <Mui.TableRow>
                  <Mui.TableCell colSpan={fields.length + 1} sx={{ ...cellSx, textAlign: "center", py: 4 }}>No rows to show.</Mui.TableCell>
                </Mui.TableRow>
              ) : (
                pageItems.map((row) => (
                  <Mui.TableRow
                    key={row.rowNumber}
                    {...(isError(row)
                      ? {
                          sx: errorRowSx,
                          tabIndex: 0,
                          title: "Click to correct this row",
                          onClick: () => onFixRow(row.rowNumber),
                          onKeyDown: (event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              onFixRow(row.rowNumber);
                            }
                          },
                        }
                      : {})}
                  >
                    <Mui.TableCell sx={cellSx}>{row.rowNumber}</Mui.TableCell>
                    {fields.map((field) => (
                      <Mui.TableCell key={field.key} sx={{ ...cellSx, whiteSpace: "nowrap" }}>{shown(row.raw[field.key])}</Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                ))
              )}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.TableContainer>
        {footer}
      </Mui.Box>
    </Mui.Stack>
  );
};

// ---- Edit an error row (opened from the preview) ----

const FIELD_HELP = {
  employee_id: { helper: "Employee code from the records.", placeholder: "e.g. VST1005" },
  employee_name: { helper: "For checking only. Not saved.", placeholder: "Employee name" },
  uan: { helper: "12 digits from the EPFO portal.", placeholder: "e.g. 100123456789" },
  pf_joined_on: { helper: "DD/MM/YYYY.", placeholder: "e.g. 01/04/2024" },
  previous_pf_member: { helper: "PF member before joining.", placeholder: "Blank = keep saved" },
  eps_member: { helper: "Member of the pension scheme.", placeholder: "Blank = keep saved" },
  dob: { helper: "DD/MM/YYYY.", placeholder: "e.g. 15/06/1990" },
  opt_out_from: { helper: "DOJ or MM/YYYY.", placeholder: "e.g. DOJ or 04/2026" },
  opt_out_reason: { helper: "Required with Opt out from.", placeholder: "Why the employee opts out" },
  extra_from_month: { helper: "MM/YYYY.", placeholder: "e.g. 04/2026" },
  full_salary: { helper: "PF on full salary, not the ceiling.", placeholder: "Blank = keep saved" },
  extra_amount: { helper: "Whole rupees per month.", placeholder: "e.g. 1000" },
};

// Side panel over the import: the row's errors on top, its mapped values
// below to correct, then Re-Verify checks the file again on the server. A
// row that passes closes the panel; otherwise the new errors show.
const RowFixPanel = ({ row, fields, onClose, onVerify }) => {
  const [form, setForm] = useState(() => ({ ...row.raw }));
  const [errors, setErrors] = useState(row.message ? [row.message] : ["This row has an error."]);
  const [busy, setBusy] = useState(false);
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));
  const yesNo = (value) => (/^y/i.test(textOf(value)) ? "Yes" : /^n/i.test(textOf(value)) ? "No" : "");

  const verify = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const next = await onVerify(row.rowNumber, form);
      if (next && !isError(next)) onClose();
      else if (next) setErrors([next.message || "This row has an error."]);
    } catch (error) {
      setErrors([`Could not check this row: ${error.message || "please try again."}`]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <GlobalSidePanel
      open
      onClose={busy ? undefined : onClose}
      hideExpand
      title="Edit UAN Row"
      subtitle={`Row ${row.rowNumber}. Correct the values and re-verify.`}
      bodySx={panelBodySx}
      footer={
        <PanelActionFooter
          onCancel={onClose}
          primaryLabel={busy ? "Verifying..." : "Re-Verify"}
          primaryProps={{ disabled: busy, loading: busy, onClick: verify }}
        />
      }
    >
      <Mui.Stack spacing={2}>
        <RequestInfoBanner
          tone="danger"
          title={errors.length > 1 ? `${errors.length} errors in this row` : "Error in this row"}
          body={errors.map((message) => (
            <Mui.Box key={message} component="span" sx={{ display: "block" }}>• {message}</Mui.Box>
          ))}
        />
        <FormSection title="PF Member" info="Values from the file. Blank cells keep what is saved.">
          {fields.map((field, index) => {
            const help = FIELD_HELP[field.key] || {};
            return (
              <Field
                key={field.key}
                label={`${field.label}${field.required ? " *" : ""}`}
                helper={help.helper}
                wide={index === fields.length - 1 && fields.length % 2 === 1}
              >
                {YES_NO_FIELDS.has(field.key) ? (
                  <SelectField value={yesNo(form[field.key])} onChange={set(field.key)} options={YES_NO_OPTIONS} placeholder={help.placeholder} />
                ) : (
                  <TextField value={textOf(form[field.key])} onChange={set(field.key)} placeholder={help.placeholder} />
                )}
              </Field>
            );
          })}
        </FormSection>
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

// ---- Result ----

const RESULT_TABS = [
  { key: "all", label: "All Rows" },
  { key: "success", label: "Imported" },
  { key: "skipped", label: "Not Imported" },
];

const ResultStep = ({ result, showName }) => {
  const rows = result.rows;
  const counts = { all: rows.length, success: rows.filter((row) => row.ok).length, skipped: rows.filter((row) => !row.ok).length };
  const [tab, setTab] = useState("all");
  const list = tab === "all" ? rows : rows.filter((row) => (tab === "success" ? row.ok : !row.ok));
  const { pageItems, footer } = usePaged(list, tab);
  const heads = ["Row", "Employee ID", ...(showName ? ["Employee"] : []), "UAN", "Status", "Message"];

  return (
    <Mui.Stack spacing={1.5}>
      <Card title="Import Complete" helper={result.message}>
        <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
          <StatCard icon={StorageOutlined} title="Total Records" value={counts.all.toLocaleString("en-IN")} accent="#12B76A" tint="#E7F8EF" onClick={() => setTab("all")} isActive={tab === "all"} activeBorder />
          <StatCard icon={CheckCircleOutline} title="Imported" value={counts.success.toLocaleString("en-IN")} accent={designColors.tealDark} tint={designColors.tealTint} onClick={() => setTab("success")} isActive={tab === "success"} activeBorder />
          <StatCard icon={CancelOutlined} title="Not Imported" value={counts.skipped.toLocaleString("en-IN")} accent={errorText} tint="#FEF2F2" onClick={() => setTab("skipped")} isActive={tab === "skipped"} activeBorder />
        </Mui.Box>
      </Card>
      <Mui.Box sx={{ ...detailCardSx, p: 0, overflow: "hidden" }}>
        <Mui.Box sx={{ p: 2 }}>
          <TabButtons tabs={RESULT_TABS} tab={tab} counts={counts} onChange={setTab} />
        </Mui.Box>
        <Mui.TableContainer sx={{ overflowX: "auto" }}>
          <Mui.Table size="small">
            <Mui.TableHead>
              <Mui.TableRow>
                {heads.map((label) => <Mui.TableCell key={label} sx={tableHeadSx}>{label}</Mui.TableCell>)}
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {pageItems.length === 0 ? (
                <Mui.TableRow>
                  <Mui.TableCell colSpan={heads.length} sx={{ ...cellSx, textAlign: "center", py: 4 }}>No rows to show.</Mui.TableCell>
                </Mui.TableRow>
              ) : (
                pageItems.map((row) => (
                  <Mui.TableRow key={row.rowNumber}>
                    <Mui.TableCell sx={cellSx}>{row.rowNumber}</Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>{shown(row.raw.employee_id)}</Mui.TableCell>
                    {showName ? <Mui.TableCell sx={cellSx}>{shown(row.raw.employee_name)}</Mui.TableCell> : null}
                    <Mui.TableCell sx={cellSx}>{shown(row.raw.uan)}</Mui.TableCell>
                    <Mui.TableCell sx={{ ...cellSx, fontWeight: designWeights.semibold, color: row.ok ? designColors.tealDark : designColors.slate500 }}>{row.ok ? "Imported" : row.status}</Mui.TableCell>
                    <Mui.TableCell sx={{ ...cellSx, whiteSpace: "normal" }}>{row.note}</Mui.TableCell>
                  </Mui.TableRow>
                ))
              )}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.TableContainer>
        {footer}
      </Mui.Box>
    </Mui.Stack>
  );
};

// ---- Wizard ----

// onImported(message): runs after the server imported the rows, so the list
// reloads. The panel stays open on the result until Done.
export const PfUanImport = ({ open, domain, companyId, onClose, onImported }) => {
  const history = useHistory();
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [mapping, setMapping] = useState({});
  // The company's saved mapping (file header -> field), from the server.
  const [savedMapping, setSavedMapping] = useState(null);
  // One request key per preview batch; Import commits that batch with it.
  const [batch, setBatch] = useState(null);
  // File rows with the server's answer: { rowNumber, raw, result, message }.
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const [actionError, setActionError] = useState("");
  const [result, setResult] = useState(null);
  const [fixRowNumber, setFixRowNumber] = useState(null);
  const fixRow = fixRowNumber == null ? null : rows.find((row) => row.rowNumber === fixRowNumber) || null;

  useEffect(() => {
    if (!open) return undefined;
    setStep(0); setFile(null); setFileError(""); setMapping({});
    setBatch(null); setRows([]); setBusy(""); setActionError("");
    setResult(null); setFixRowNumber(null); setSavedMapping(null);
    const controller = new AbortController();
    fetchUanMapping({ domain, companyId, signal: controller.signal })
      .then((saved) => setSavedMapping(saved))
      .catch(() => {}); // Auto-mapping still works without it.
    return () => controller.abort();
  }, [open, domain, companyId]);

  // Preview and Edit panel show only the mapped fields.
  const mappedFields = useMemo(() => {
    const mapped = new Set(Object.values(mapping));
    return UAN_IMPORT_FIELDS.filter((field) => mapped.has(field.key));
  }, [mapping]);
  const toSave = rows.filter(willSave);

  const mapFor = (headers) => {
    const auto = autoMapUanColumns(headers);
    if (!savedMapping) return auto;
    const remembered = {};
    headers.forEach((header, index) => {
      const key = savedMapping[normalise(header)];
      if (key) remembered[index] = key;
    });
    return Object.keys(remembered).length ? remembered : auto;
  };

  const handleFile = async (picked) => {
    setFileError("");
    const extension = picked.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(extension)) {
      setFile(null);
      setFileError("Unsupported file type. Upload an Excel (.xlsx, .xls) or CSV (.csv) file.");
      return;
    }
    if (picked.size > MAX_BYTES) {
      setFile(null);
      setFileError("The file is larger than 5 MB.");
      return;
    }
    try {
      const [headerRow = [], ...body] = await readSheet(picked);
      const headers = headerRow.map((cell) => String(cell ?? "").trim());
      const fileRows = body.filter((row) => row.some((cell) => String(cell ?? "").trim() !== ""));
      if (!headers.some(Boolean) || !fileRows.length) {
        setFile(null);
        setFileError("The file has no data rows under the header row.");
        return;
      }
      if (fileRows.length > MAX_ROWS) {
        setFile(null);
        setFileError(`The file has ${fileRows.length.toLocaleString("en-IN")} rows. Import up to 5,000 at a time.`);
        return;
      }
      setFile({ name: picked.name, size: picked.size, headers, rows: fileRows });
      setMapping(mapFor(headers));
    } catch (error) {
      setFile(null);
      setFileError(error.message);
    }
  };

  const downloadTemplate = async () => {
    if (busy) return;
    setBusy("template");
    try {
      await downloadUanTemplate({ domain, companyId, sampleRows: TEMPLATE_SAMPLE_ROWS });
    } catch (error) {
      showToast("error", error.message || "Could not download the template.");
    } finally {
      setBusy("");
    }
  };

  // Sends every row as a new preview batch and returns the rows with the
  // server's answer. Throws when the server can't be reached.
  const previewRows = async (current) => {
    const requestKey = newRequestKey();
    const answer = await previewUanImport({
      domain,
      companyId,
      requestKey,
      fileName: file.name,
      rows: current.map((row) => row.raw),
      mapping: mappingByHeader(file.headers, mapping),
    });
    setBatch({ id: answer.batchId, requestKey });
    const checked = withResults(current, answer.results);
    setRows(checked);
    return checked;
  };

  // Map Columns > Next: the server checks the mapped rows, then preview.
  const handleMapNext = async () => {
    if (busy) return;
    setBusy("preview");
    setActionError("");
    try {
      const raws = mappedUanRows(file.rows, mapping);
      await previewRows(raws.map((raw, index) => ({ rowNumber: index + 1, raw })));
      setStep(2);
    } catch (error) {
      setActionError(error.message || "Could not check the file. Please try again.");
    } finally {
      setBusy("");
    }
  };

  // Edit panel > Re-Verify: the corrected row goes back into the file and
  // the whole file is checked again as a new batch (Import commits a batch).
  const verifyRow = async (rowNumber, raw) => {
    const next = rows.map((row) => (row.rowNumber === rowNumber ? { rowNumber, raw } : { rowNumber: row.rowNumber, raw: row.raw }));
    const checked = await previewRows(next);
    return checked.find((row) => row.rowNumber === rowNumber);
  };

  const runImport = async () => {
    if (busy || !batch || !toSave.length) return;
    setBusy("import");
    setActionError("");
    try {
      const answer = await commitUanImport({ domain, companyId, batchId: batch.id, requestKey: batch.requestKey });
      const message = answer.message || `${answer.imported ?? toSave.length} rows imported.`;
      setResult({
        message,
        rows: rows.map((row) => ({
          rowNumber: row.rowNumber,
          raw: row.raw,
          ok: willSave(row),
          status: isError(row) ? "Error" : "Skipped",
          note: willSave(row) ? (row.result === "Change" ? "Updated" : "Added") : isError(row) ? row.message || "Has errors" : "Same as saved",
        })),
      });
      onImported?.(message);
    } catch (error) {
      setActionError(error.message || "Could not import. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const canContinue = (step === 0 && Boolean(file)) || (step === 1 && missingUanFields(mapping).length === 0);

  const footer = result ? (
    <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
      <PrimaryButton onClick={onClose}>Done</PrimaryButton>
    </Mui.Stack>
  ) : (
    <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} justifyContent="space-between" sx={{ width: "100%" }}>
      <SecondaryButton disabled={Boolean(busy)} onClick={onClose}>Cancel</SecondaryButton>
      <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5}>
        {step > 0 ? (
          <SecondaryButton disabled={Boolean(busy)} startIcon={<ArrowBack />} onClick={() => { setActionError(""); setStep(step - 1); }}>
            Back
          </SecondaryButton>
        ) : null}
        {step < 2 ? (
          <PrimaryButton
            endIcon={<ArrowForward />}
            disabled={!canContinue || Boolean(busy)}
            loading={step === 1 && busy === "preview"}
            onClick={step === 1 ? handleMapNext : () => setStep(step + 1)}
          >
            {step === 1 && busy === "preview" ? "Validating..." : "Next"}
          </PrimaryButton>
        ) : (
          <Mui.Tooltip arrow placement="top" slotProps={tooltipStyleProps} title={toSave.length ? "" : "No new or changed rows to import."}>
            <span>
              <PrimaryButton startIcon={<FileUploadOutlined />} disabled={!toSave.length || Boolean(busy)} loading={busy === "import"} onClick={runImport}>
                Import {toSave.length ? toSave.length.toLocaleString("en-IN") : ""}
              </PrimaryButton>
            </span>
          </Mui.Tooltip>
        )}
      </Mui.Stack>
    </Mui.Stack>
  );

  const subtitles = [
    "Upload a file to add or update UAN and PF details for PF members.",
    "Map your file columns to the PF member fields.",
    "Review your data and import.",
  ];

  return (
    <GlobalSidePanel
      open={open}
      onClose={busy === "import" ? undefined : onClose}
      expanded={open}
      onToggleExpand={() => {}}
      hideExpand
      title="Import UAN"
      subtitle={result ? "Import result for each record." : subtitles[step]}
      // Payroll -> Payroll home; PF -> back to the list (closes this panel).
      breadcrumb={[
        { label: "Payroll", onClick: () => { if (busy !== "import") history.push("/admin/payroll/setup"); } },
        "Statutory",
        { label: "PF", onClick: () => { if (busy !== "import") onClose(); } },
        "Import",
      ]}
      bodySx={{ backgroundColor: designColors.pageBg, p: { xs: 1.5, sm: 2 } }}
      footer={footer}
    >
      <Mui.Stack spacing={1.5}>
        {result ? (
          <ResultStep result={result} showName={mappedFields.some((field) => field.key === "employee_name")} />
        ) : (
          <>
            <Mui.Box sx={{ ...detailCardSx, py: 1 }}>
              <WizardStepper steps={STEPS} step={step} />
            </Mui.Box>
            {step === 0 ? (
              <UploadStep file={file} error={fileError} onFile={handleFile} onTemplate={downloadTemplate} templateBusy={busy === "template"} />
            ) : step === 1 ? (
              <MapStep
                file={file}
                mapping={mapping}
                onMappingChange={(index, fieldKey) =>
                  setMapping((current) => {
                    const next = { ...current };
                    if (fieldKey) next[index] = fieldKey;
                    else delete next[index];
                    return next;
                  })
                }
                onReset={() => setMapping(autoMapUanColumns(file.headers))}
                onChangeFile={() => setStep(0)}
              />
            ) : (
              <PreviewStep rows={rows} fields={mappedFields} onFixRow={setFixRowNumber} />
            )}
          </>
        )}
        {/* Errors sit at the bottom, next to the buttons. */}
        {actionError ? <RequestInfoBanner tone="danger" title="Something went wrong" body={actionError} /> : null}
      </Mui.Stack>
      {/* key: a fresh form for each row opened. */}
      {fixRow && step === 2 && !result ? <RowFixPanel key={fixRow.rowNumber} row={fixRow} fields={mappedFields} onClose={() => setFixRowNumber(null)} onVerify={verifyRow} /> : null}
    </GlobalSidePanel>
  );
};
